import React from 'react';

export interface WizardStep {
  key: string;
  title: string;
  subtitle: string;
  hidden?: boolean; // skip in the workflow list (keeps original indices intact)
  done?: boolean;   // completion state — when set, drives the green tick instead of scroll position
  warn?: boolean;   // reached but needs input — amber "attention" state (e.g. no services yet)
  error?: boolean;  // has an invalid entry — red "error" state (e.g. description too short)
}

interface InvoiceWizardStepperProps {
  steps: WizardStep[];
  activeStep: number;      // 0-based — the section currently in view (scroll-spy)
  maxReached: number;      // highest step index reached (all clickable in scroll mode)
  onStepClick: (index: number) => void;
  stepOfLabel: string;     // e.g. "Step 3 of 6" (mobile)
  workflowLabel?: string;  // sidebar heading
}

const CheckIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M13.5 4.5L6.5 11.5L2.5 7.5" stroke="currentColor" strokeWidth="2.2"
      strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const InvoiceWizardStepper: React.FC<InvoiceWizardStepperProps> = ({
  steps,
  activeStep,
  maxReached,
  onStepClick,
  stepOfLabel,
  workflowLabel = 'Workflow',
}) => {
  const total = steps.length;
  const pct = Math.round(((activeStep + 1) / total) * 100);

  // Keep the active chip smoothly centred in the mobile chips strip (horizontal only)
  const chipsRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const container = chipsRef.current;
    if (!container) return;
    const active = container.querySelector<HTMLElement>('.iw-chip.is-active');
    if (!active) return;
    const target = active.offsetLeft - (container.clientWidth - active.offsetWidth) / 2;
    container.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
  }, [activeStep]);

  return (
    <div className="iw-stepper iw-stepper--vertical">
      {/* Desktop: vertical workflow timeline (scroll-spy) */}
      <div className="iw-vstepper" role="list" aria-label={workflowLabel}>
        <div className="iw-workflow-label">{workflowLabel}</div>
        <div className="iw-vsteps">
          {steps.map((step, index) => {
            if (step.hidden) return null;
            const isActive = index === activeStep;
            // A step is "done" (green tick) only when actually complete — driven by
            // step.done when provided, otherwise by scroll position. Never the active step.
            const isDone = !isActive && (step.done !== undefined ? step.done : index < activeStep);
            const isError = !isActive && !isDone && !!step.error;
            const isWarn = !isActive && !isDone && !isError && !!step.warn;
            const isClickable = index <= maxReached;
            return (
              <button
                type="button"
                key={step.key}
                role="listitem"
                className={`iw-vstep${isActive ? ' is-active' : ''}${isDone ? ' is-done' : ''}${isWarn ? ' is-warn' : ''}${isError ? ' is-error' : ''}${isClickable ? ' is-clickable' : ''}`}
                aria-current={isActive ? 'step' : undefined}
                disabled={!isClickable}
                onClick={() => isClickable && onStepClick(index)}
              >
                <span className="iw-vstep-marker">
                  <span className="iw-vstep-dot">{isDone ? <CheckIcon /> : null}</span>
                </span>
                <span className="iw-vstep-text">
                  <span className="iw-vstep-title">{step.title}</span>
                  <span className="iw-vstep-sub">{step.subtitle}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Mobile: scrollable chips */}
      <div className="iw-stepper-mobile">
        <div className="iw-chips" ref={chipsRef}>
          {steps.map((step, index) => {
            if (step.hidden) return null;
            const isActive = index === activeStep;
            const isDone = !isActive && (step.done !== undefined ? step.done : index < activeStep);
            const isError = !isActive && !isDone && !!step.error;
            const isWarn = !isActive && !isDone && !isError && !!step.warn;
            const isClickable = index <= maxReached;
            return (
              <button
                type="button"
                key={step.key}
                className={`iw-chip${isActive ? ' is-active' : ''}${isDone ? ' is-done' : ''}${isWarn ? ' is-warn' : ''}${isError ? ' is-error' : ''}`}
                aria-current={isActive ? 'step' : undefined}
                disabled={!isClickable}
                onClick={() => isClickable && onStepClick(index)}
              >
                <span className="iw-chip-num">{isDone ? <CheckIcon /> : index + 1}</span>
                <span className="iw-chip-label">{step.title}</span>
              </button>
            );
          })}
        </div>

      </div>
    </div>
  );
};

export default InvoiceWizardStepper;
